"use client";

import { type FormEvent, useEffect, useRef, useState } from "react";
import { usePortalTranslations } from "../i18n/client";

export function AccountSettingsDialog({
  identity,
  onClose,
}: {
  identity: { name: string; email: string };
  onClose: () => void;
}) {
  const t = usePortalTranslations();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialog = useRef<HTMLElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.querySelector<HTMLInputElement>("input")?.focus();

    function trapFocus(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const elements = [...(dialog.current?.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled), a[href], [tabindex='0']") ?? [])];
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }

    document.addEventListener("keydown", trapFocus);
    return () => {
      document.removeEventListener("keydown", trapFocus);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, []);

  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !busy) onClose();
    }

    document.addEventListener("keydown", closeOnEscape);

    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const form = new FormData(event.currentTarget);

    try {
      const response = await fetch("/api/client-portals/_directory/account", {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: form.get("name"),
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null) as { message?: string } | null;
        const translatedMessage = response.status === 401
          ? t("profile.sessionExpired")
          : body?.message === "Current password is incorrect."
          ? t("profile.incorrectPassword")
          : body?.message === "Passwords do not match or are too short."
            ? t("profile.passwordMismatch")
            : response.status === 422
              ? t("profile.invalidDetails")
              : t("profile.saveFailed");
        throw new Error(translatedMessage);
      }

      window.location.reload();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : t("profile.saveFailed"));
      setBusy(false);
    }
  }

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <section ref={dialog} className="account-settings" role="dialog" aria-modal="true" aria-labelledby="account-settings-title">
        <header className="account-settings__header">
          <div>
            <h2 id="account-settings-title">{t("profile.settings")}</h2>
            <p>{t("profile.sharedIdentity")}</p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label={t("profile.close")}>×</button>
        </header>

        <form className="account-settings__form" onSubmit={submit}>
          {error ? <p className="form-error" role="alert">{error}</p> : null}

          <label>
            <span>{t("profile.name")}</span>
            <input name="name" defaultValue={identity.name} required maxLength={160} autoComplete="name" disabled={busy} />
          </label>
          <label>
            <span>{t("profile.email")}</span>
            <input value={identity.email} readOnly type="email" autoComplete="email" />
          </label>

          <div className="account-settings__password">
            {/* This route redirects to another origin and must use a full browser navigation. */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a href="/auth/account">{t("profile.security")}</a>
            <p>{t("profile.securityDescription")}</p>
          </div>

          <footer className="account-settings__actions">
            <button type="button" onClick={onClose} disabled={busy}>{t("profile.cancel")}</button>
            <button type="submit" className="primary-button" disabled={busy}>
              {t(busy ? "profile.saving" : "profile.save")}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
